import { useState, useRef, useCallback, useEffect } from 'react';
import { getDB } from '../lib/db';
import api from '../lib/api';
import { haversineDistance } from '../utils/geo';

const PING_INTERVAL_MS = 60_000; // 60 seconds
const JITTER_THRESHOLD_KM = 0.01; // 10 meters — matches backend
const BATCH_SIZE = 50;

interface Position {
  latitude: number;
  longitude: number;
  accuracy: number | null;
}

interface UseLocationTrackingReturn {
  isTracking: boolean;
  lastPosition: Position | null;
  totalDistanceKm: number;
  pingCount: number;
  start: () => void;
  stop: () => Promise<void>;
}

export function useLocationTracking(): UseLocationTrackingReturn {
  const [isTracking, setIsTracking] = useState(false);
  const [lastPosition, setLastPosition] = useState<Position | null>(null);
  const [totalDistanceKm, setTotalDistanceKm] = useState(0);
  const [pingCount, setPingCount] = useState(0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevPositionRef = useRef<{ lat: number; lng: number } | null>(null);
  const distanceRef = useRef(0);
  const countRef = useRef(0);

  const getBatteryLevel = async (): Promise<number | null> => {
    try {
      const battery = await (navigator as any).getBattery?.();
      return battery ? Math.round(battery.level * 100) : null;
    } catch {
      return null;
    }
  };

  const storePing = async (
    lat: number,
    lng: number,
    accuracy: number | null,
    batteryLevel: number | null,
    speed: number | null
  ) => {
    const db = await getDB();
    const id = crypto.randomUUID();
    await db.put('location_pings', {
      id,
      latitude: lat,
      longitude: lng,
      accuracy,
      battery_level: batteryLevel,
      speed,
      timestamp: new Date().toISOString(),
      synced: 0,
    });
  };

  const syncPings = useCallback(async () => {
    if (!navigator.onLine) return;

    try {
      const db = await getDB();
      const unsynced = await db.getAllFromIndex('location_pings', 'by-synced', 0);

      if (unsynced.length === 0) return;

      // Send in batches
      for (let i = 0; i < unsynced.length; i += BATCH_SIZE) {
        const batch = unsynced.slice(i, i + BATCH_SIZE);
        const locations = batch.map((p) => ({
          latitude: p.latitude,
          longitude: p.longitude,
          accuracy: p.accuracy ?? undefined,
          battery_level: p.battery_level ?? undefined,
          speed: p.speed ?? undefined,
          timestamp: p.timestamp,
        }));

        try {
          await api.post('/tracking/locations/batch', { locations });

          // Mark as synced
          const tx = db.transaction('location_pings', 'readwrite');
          for (const ping of batch) {
            await tx.store.put({ ...ping, synced: 1 });
          }
          await tx.done;
        } catch {
          // Will retry on next sync cycle
          break;
        }
      }
    } catch {
      // Silent — will retry
    }
  }, []);

  const recordPosition = useCallback(async () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy, speed } = pos.coords;
        const batteryLevel = await getBatteryLevel();

        setLastPosition({ latitude, longitude, accuracy });

        // Calculate distance from previous point
        if (prevPositionRef.current) {
          const delta = haversineDistance(
            prevPositionRef.current.lat,
            prevPositionRef.current.lng,
            latitude,
            longitude
          );
          if (delta >= JITTER_THRESHOLD_KM) {
            distanceRef.current += delta;
            setTotalDistanceKm(distanceRef.current);
            prevPositionRef.current = { lat: latitude, lng: longitude };
          }
        } else {
          prevPositionRef.current = { lat: latitude, lng: longitude };
        }

        countRef.current += 1;
        setPingCount(countRef.current);

        await storePing(latitude, longitude, accuracy, batteryLevel, speed);

        // Try to sync after every ping
        await syncPings();
      },
      () => {
        // GPS error — skip this tick
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
    );
  }, [syncPings]);

  const start = useCallback(() => {
    if (intervalRef.current) return;

    setIsTracking(true);
    distanceRef.current = 0;
    countRef.current = 0;
    prevPositionRef.current = null;
    setTotalDistanceKm(0);
    setPingCount(0);

    // Record immediately, then every PING_INTERVAL_MS
    recordPosition();
    intervalRef.current = setInterval(recordPosition, PING_INTERVAL_MS);
  }, [recordPosition]);

  const stop = useCallback(async () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsTracking(false);

    // Final sync attempt
    await syncPings();
  }, [syncPings]);

  // Sync when coming back online
  useEffect(() => {
    const handleOnline = () => {
      if (isTracking) syncPings();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [isTracking, syncPings]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    isTracking,
    lastPosition,
    totalDistanceKm,
    pingCount,
    start,
    stop,
  };
}
